<template>
  <BaseLayout>
    <TimelineLoading id="timelineLoading" :loading="isLoading" step="PAGAMENTO">
      <div class="flex w-full sm:w-2/3 xl:w-1/2 items-center justify-center">
        <OfferDetailContext step="PAGAMENTO" id="comp-pix-plan-detail" :offer="offer" :isAccordionOpen="false" :keepAccordionOpen="false" :colorSolid="false">
          <div id="container" class="flex flex-col w-full h-full items-center justify-between gap-5">
            <mdn-text class="text-2xl font-semibold text-gray-700 mt-5">Copie o código Pix e cole em seu app de pagamento</mdn-text>
            <mdn-text id="text-pix-expiration-date" class="text-base text-gray-500 font-medium">
              Pague até {{ dataExpiracaoFormatada }}
            </mdn-text>
            <qrcode-vue id="comp-pix-qrCode" :value="pix.qrCode" size="220"/>
            <div class=" border-solid border p-1 border-gray-500 rounded-lg flex flex-row justify-between items-center w-full">
              <mdn-text id="text-pix-qrCode" class="text-s text-gray-500 font-medium truncate">{{ pix.qrCode }}</mdn-text>
              <span class="text-green-500 mdn-Icon-pix mdn-Icon--md"></span>
            </div>
            <Button id="btn-pix-copy-pix" @click="onCopyButtonClicked" class="w-full" global>  Copiar código Pix </Button>
            <div class="flex flex-row justify-center items-center">
              <span class="text-red-400 mdn-Icon-circulo-exclamacao mdn-Icon--md"></span>
              <mdn-text id="text-pix-billing-tip" class="text-xs text-gray-500 font-medium ml-2">
                A primeira cobrança será no Pix e a partir do próximo mês as cobranças serão feitas no cartão cadastrado
                {{ card?.brand }} de final {{ card?.last }}.
              </mdn-text>
            </div>
            <Accordion id="comp-pix-paying-tutorial" class="mb-5 border-solid border w-full" :defaultOpen=false>
              <mdn-text class="px-3 text-lg font-medium text-gray-600" slot="AccordionFancyTitle">Saiba como pagar com Pix</mdn-text>
              <div class="flex flex-col gap-3">
                <div class="flex flex-row justify-start items-center gap-2">
                  <div class="flex w-9 h-9 justify-center items-center border-solid border-gray-300 border-2 rounded-full">
                    <span class="flex justify-center items-center text-gray-400 mdn-Icon-duplicar mdn-Icon--md w-9 h-9"></span>
                  </div>
                  <mdn-text id="comp-pix-paying-tutorial-1" class="text-lg font-normal text-gray-500 ml-1">Copie o código Pix que foi gerado</mdn-text>
                </div>
                <div class="flex flex-row justify-start items-center gap-2">
                  <div class="flex w-9 h-9 justify-center items-center border-solid border-gray-300 border-2 rounded-full">
                    <span class="flex justify-center items-center text-gray-400 mdn-Icon-celular mdn-Icon--md w-9 h-9" aria-hidden="true"></span>
                  </div>
                  <mdn-text id="comp-pix-paying-tutorial-2" class="text-lg font-normal text-gray-500 ml-1">Abra o app de pagamento e acesse a seção Pix</mdn-text>
                </div>
                <div class="flex flex-row justify-start items-center gap-2">
                  <div class="flex w-9 h-9 justify-center items-center border-solid border-gray-300 border-2 rounded-full">
                    <span class="flex justify-center items-center text-gray-400 mdn-Icon-check mdn-Icon--md w-9 h-9"></span>
                  </div>
                  <mdn-text id="comp-pix-paying-tutorial-3" class="text-lg font-normal text-gray-500 ml-1">Cole o código na opção "Pix Copia e Cola" e confirme o pagamento</mdn-text>
                </div>
              </div>
            </Accordion>
          </div>
        </OfferDetailContext>
      </div>
    </TimelineLoading>
  </BaseLayout>
</template>

<script>
import { singleOfferMixin } from 'business/offers/mixins'
import { loadUserDataMixin } from 'business/user/mixins'
import { serviceBuilder as paymentService } from '@/business/payment/service'
import { serviceBuilder as userService } from '@/business/user/service'
import {
  managePixTransactionMixin, manageTransactionIdMixin, manageConfirmationMixin, manageCardMixin
} from 'business/payment/mixins'
import { useHttpServiceMixin } from '@/common/use-http'
import store from '@/business/store'
import { NotifyUserActionsEnum } from '@/business/http/dto/NotifyUserActionRequest'
import { SMS_STORE_MSISDN_KEY, SMS_STORE_ORIGIN_KEY } from '@/common/constants'
import { MdnText, Button } from 'mondrian-vue'
import TimelineLoading from '../../components/TimelineLoading.vue'
import OfferDetailContext from '../../components/OfferDetailContext.vue'
import { PixTransaction } from '../../business/domain/PixTransaction'
import dayjs from 'dayjs'
import QrcodeVue from 'qrcode.vue'
import Accordion from '../../components/Accordion.vue'
import { SuccessfulTransaction } from '../../business/domain/SuccessfulTransaction'
import { Card } from '../../business/domain/Card'

export default {
  name: 'page-3-step-pix',
  title: 'Pix',
  components: {
    MdnText,
    Button,
    TimelineLoading,
    OfferDetailContext,
    QrcodeVue,
    Accordion
  },
  mixins: [
    singleOfferMixin,
    loadUserDataMixin,
    managePixTransactionMixin,
    manageConfirmationMixin,
    manageCardMixin,
    manageTransactionIdMixin,
    useHttpServiceMixin({ paymentService, userService })
  ],
  onUserDataError: {
    name: 'page-2-step'
  },
  data: () => ({
    pix: PixTransaction,
    card: Card,
    isProcessing: false,
    shouldShowCopyAlert: false,
    tid: null,
    lastSchedule: new Set(),
    isStillMounted: true
  }),
  computed: {
    isLoading () {
      return this.isOfferLoading || this.isProcessing
    },
    dataExpiracaoFormatada () {
      return dayjs(new Date(this.pix.dataExpiracao)).format('DD/MM/YYYY, [às] HH:mm') ?? null
    }
  },
  methods: {
    async notifyActionPixSuccess () {
      const { offerId } = this
      const msisdn = await store.get(SMS_STORE_MSISDN_KEY)
      const origin = await store.get(SMS_STORE_ORIGIN_KEY)
      const extra = { origin, pix: true, offerId }
      await this.$userService.notifyUserAction(msisdn, NotifyUserActionsEnum.ADESAO, extra)
    },
    async onCopyButtonClicked () {
      await navigator.clipboard.writeText(this.pix.qrCode)
      this.$notify({ title: 'Código copiado com sucesso' })
    },
    async pollVerifyTransaction () {
      try {
        const result = await this.$paymentService.verifyTransaction(this.tid)
        if (result.done) {
          const confirmation = new SuccessfulTransaction(
            dayjs(),
            this.card.last,
            this.card.brand,
            result.msisdn,
            true
          )
          await this.storeSuccessfulTransaction(confirmation)
          this.notifyActionPixSuccess()
          this.$tracker.log('tentativa:migracao', 'sucesso')
          this.$router.replace({
            name: 'page-success'
          })
        } else {
          if (this.isStillMounted) {
            this.lastSchedule.add(setTimeout(() => this.pollVerifyTransaction(), 4000))
          }
        }
      } catch (e) {}
    }
  },
  async mounted () {
    this.tid = await this.loadTransactionId()
    this.pix = await this.loadPixTransaction()
    this.card = await this.loadCard()
    setTimeout(() => this.pollVerifyTransaction(), 1000)
  },
  destroyed () {
    this.isStillMounted = false
    this.lastSchedule.forEach(id => clearTimeout(id))
  }
}
</script>
